import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ChartDisplay from '../ChartDisplay/ChartDisplay';
import NivoChartRenderer from '../ChartDisplay/NivoChartRenderer';
import AddToDashboardButton from '../ChartDisplay/AddToDashboardButton';
import { transformChartData, formatStepLabel } from '../../utils/chartHelpers';
import './GraphModal.css';
import '../ChartDisplay/ChartTypeSelector.css';

/**
 * Full-screen modal for displaying charts
 * @param {Object} props - Component props
 * @param {boolean} props.isOpen - Whether modal is open
 * @param {function} props.onClose - Callback to close modal
 * @param {Object} props.message - Message with chart data (for chat messages)
 * @param {Array} props.messages - All messages (for ChartDisplay)
 * @param {Object} props.chartData - Already-transformed chart data (for widgets)
 * @param {string} props.queryId - Query ID (for widgets)
 * @param {string} props.selectedChartType - Initial chart type (for widgets)
 * @returns {JSX.Element} GraphModal component
 */
const GraphModal = ({ isOpen, onClose, message, stepIndex = 0, messages, chartData: externalChartData, queryId: externalQueryId, selectedChartType: externalSelectedChartType }) => {
  const { t } = useTranslation();
  const [chartData, setChartData] = useState(null);
  const [selectedChartType, setSelectedChartType] = useState(null);
  const [queryId, setQueryId] = useState(null);
  const [localStepIndex, setLocalStepIndex] = useState(stepIndex);

  // Non-null steps derived from the message data array (for chat message mode)
  const nonNullSteps = message?.metadata?.data
    ? message.metadata.data.map((d, i) => ({ d, i })).filter(s => s.d != null)
    : [];

  // Keep localStepIndex in sync when the modal is opened with a specific step
  useEffect(() => {
    setLocalStepIndex(stepIndex);
  }, [stepIndex, isOpen]);

  // Extract chart data from either message (chat) or direct props (widgets)
  useEffect(() => {
    if (externalChartData) {
      // Widget mode: use already-transformed data directly
      setChartData(externalChartData);
      setSelectedChartType(externalSelectedChartType || externalChartData.chartConfig?.chart_type || 'table');
      setQueryId(externalQueryId || null);
    } else if (message?.metadata?.data) {
      // Chat message mode: pick the correct step from the data array
      const dataArr = message.metadata.data;
      const stepData = Array.isArray(dataArr) ? dataArr[localStepIndex] : dataArr;
      const transformedData = transformChartData(stepData);
      if (transformedData) {
        setChartData(transformedData);
        setSelectedChartType(transformedData.chartConfig?.chart_type || 'table');
        const rawQueryId = message.metadata.queryId;
        setQueryId(Array.isArray(rawQueryId) ? (rawQueryId[localStepIndex] || null) : (rawQueryId || null));
      }
    } else {
      // Reset if neither is provided
      setChartData(null);
      setSelectedChartType(null);
      setQueryId(null);
    }
  }, [message, localStepIndex, externalChartData, externalQueryId, externalSelectedChartType]);

  // Handle ESC key to close
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = '';
    };
  }, [isOpen, onClose]);

  // Translate chart type names
  const translateChartType = (type) => {
    return t(`charts.chartTypes.${type}`) || type.charAt(0).toUpperCase() + type.slice(1);
  };

  if (!isOpen) {
    return null;
  }

  // Use externalChartData directly for widgets, or chartData for messages
  const currentChartData = externalChartData || chartData;
  const currentQueryId = externalQueryId || queryId;
  const currentSelectedChartType = selectedChartType || externalSelectedChartType;

  return (
    <div className="graph-modal-overlay" onClick={onClose}>
      <div className="graph-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="graph-modal-header">
          <div className="graph-modal-header-left">
            {!externalChartData && nonNullSteps.length > 0 && (
              <div className="inline-chart-type-selector">
                <select
                  className="chart-type-select"
                  value={localStepIndex}
                  onChange={(e) => setLocalStepIndex(Number(e.target.value))}
                >
                  {nonNullSteps.map(({ d, i }, tabIdx) => (
                    <option key={i} value={i}>
                      {formatStepLabel(d?.metadata?.data_sources, tabIdx, t)}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>
          <div className="graph-modal-header-right">
            {currentChartData?.chartTypes && currentChartData.chartTypes.length > 1 && (
              <div className="inline-chart-type-selector">
                <select
                  value={currentSelectedChartType || ''}
                  onChange={(e) => setSelectedChartType(e.target.value)}
                  className="chart-type-select"
                >
                  {currentChartData.chartTypes.map((type) => (
                    <option key={type} value={type}>
                      {translateChartType(type)}
                    </option>
                  ))}
                </select>
              </div>
            )}
            {currentQueryId && (
              <AddToDashboardButton 
                queryId={currentQueryId}
                currentChartType={currentSelectedChartType}
                disabled={!currentChartData}
              />
            )}
            <button
              className="graph-modal-close"
              onClick={onClose}
              aria-label="Close"
              title="Close (ESC)"
            >
              ×
            </button>
          </div>
        </div>
        <div className="graph-modal-body">
          {externalChartData ? (
            // Widget mode: use NivoChartRenderer directly with already-transformed data
            <NivoChartRenderer
              data={externalChartData.data}
              chartType={currentSelectedChartType || externalChartData.chartConfig?.chart_type || 'table'}
              columns={externalChartData.columns}
              dataType={externalChartData.dataType}
              chartTypes={externalChartData.chartTypes}
              seriesInfo={externalChartData.seriesInfo}
              metadata={externalChartData.metadata}
              columnDisplayNames={externalChartData.columnDisplayNames}
              columnValueTypes={externalChartData.columnValueTypes}
              chartConfig={externalChartData.chartConfig}
              onChartTypeChange={setSelectedChartType}
              queryId={currentQueryId}
              hideHeader={true}
            />
          ) : (
            // Chat message mode: use ChartDisplay with messages
            <ChartDisplay 
              messages={messages} 
              selectedMessageId={message?.id}
              hideHeader={true}
              externalChartType={currentSelectedChartType}
              onChartTypeChange={setSelectedChartType}
              externalStepIndex={localStepIndex}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default GraphModal;
